import { resolveErrorText } from '@/api/errors';
import {
  useAllergenTable,
  useSetAllergenTable,
} from '@/features/allergen-labeling/hooks/useAllergenLabeling';
import { mergeLegend, missingLegend, type LegendEntry } from '@/features/menu/utils/menu-legend';
import { getErrorT, useT } from '@/i18n';
import { Button, Preloader, f7 } from 'framework7-react';
import { useState } from 'react';

export function MenuLegendOffer({
  legend,
  onAdded,
}: {
  legend: LegendEntry[];
  onAdded: () => void;
}) {
  const t = useT();
  const c = t.menuChecklist;
  const table = useAllergenTable();
  const setTable = useSetAllergenTable();
  const [open, setOpen] = useState(false);
  const additions = table.data ? missingLegend(legend, table.data) : [];
  if (additions.length === 0) return null;

  const add = async () => {
    if (setTable.isPending) return;
    try {
      const fresh = await table.refetch();
      const current = fresh.data ?? table.data ?? [];
      const toAdd = missingLegend(legend, current);
      if (toAdd.length > 0) await setTable.mutateAsync(mergeLegend(current, toAdd));
      setOpen(false);
      onAdded();
    } catch (error) {
      f7.toast.show({ text: resolveErrorText(error, getErrorT()), closeTimeout: 4000 });
    }
  };

  return (
    <div className="menu-legend" data-testid="menu-legend">
      {!open ? (
        <button
          type="button"
          className="menu-legend__offer"
          onClick={() => setOpen(true)}
          data-testid="menu-legend-offer"
        >
          <i className="f7-icons" aria-hidden="true">
            plus_circle
          </i>
          <span>{c.addLegend(additions.length)}</span>
        </button>
      ) : (
        <div className="menu-legend__panel">
          <p className="menu-legend__intro">{c.legendIntro}</p>
          <ul className="menu-legend__codes" data-testid="menu-legend-codes">
            {additions.map((entry) => (
              <li key={entry.shortKey} data-testid={`menu-legend-code-${entry.shortKey}`}>
                <strong>{entry.shortKey}</strong>
                <span className="menu-legend__description">{entry.description || '—'}</span>
                <span className="menu-legend__kind">
                  {t.menu.allergenLabeling.kindLabel(entry.kind)}
                </span>
              </li>
            ))}
          </ul>
          <div className="menu-legend__actions">
            <Button
              round
              className="menu-legend__cancel"
              onClick={() => setOpen(false)}
              data-testid="menu-legend-cancel"
            >
              {t.cancel}
            </Button>
            <Button
              round
              fill
              disabled={setTable.isPending}
              onClick={() => void add()}
              data-testid="menu-legend-add"
            >
              {setTable.isPending ? <Preloader size={18} color="white" /> : c.addCodes}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
